import React, { useState, useRef } from "react";
import instantNewsletterService from "../../../services/instantNewsletterService";

interface AuthorStepProps {
  onConfirm: (authorName: string | null, authorImageUrl: string | null) => void;
  isLoading: boolean;
}

const AuthorStep: React.FC<AuthorStepProps> = ({ onConfirm, isLoading }) => {
  const [name, setName] = useState("");
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [previewSrc, setPreviewSrc] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setUploadError("Please select an image file.");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setUploadError("Image must be under 2MB.");
      return;
    }

    setUploadError(null);
    setPreviewSrc(URL.createObjectURL(file));
    setUploading(true);

    try {
      const result = await instantNewsletterService.uploadAuthorImage(file);
      setImageUrl(result.url);
    } catch (err: any) {
      setUploadError(err.response?.data?.error || "Upload failed.");
      setPreviewSrc(null);
      setImageUrl(null);
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = () => {
    setPreviewSrc(null);
    setImageUrl(null);
    setUploadError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleContinue = () => {
    onConfirm(
      name.trim() || null,
      imageUrl || null,
    );
  };

  const hasAnyInput = name.trim() || imageUrl;

  return (
    <div style={{ textAlign: "center", maxWidth: "440px", width: "100%" }}>
      <h2 style={{ fontSize: "1.5rem", marginBottom: "0.25rem" }}>
        Personalize your newsletter
      </h2>
      <p style={{ color: "#666", marginBottom: "1.5rem", fontSize: "0.9rem" }}>
        Add your name and photo so readers know who it's from. This is optional.
      </p>

      {/* Author name */}
      <div style={{ marginBottom: "1.25rem", textAlign: "left" }}>
        <label style={{ fontSize: "0.85rem", fontWeight: 600, display: "block", marginBottom: "6px" }}>
          Your name
        </label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Sarah Johnson"
          style={{
            width: "100%",
            padding: "10px 12px",
            fontSize: "0.95rem",
            border: "1px solid #ddd",
            borderRadius: "8px",
            outline: "none",
            boxSizing: "border-box",
          }}
        />
      </div>

      {/* Author image */}
      <div style={{ marginBottom: "1.5rem", textAlign: "left" }}>
        <label style={{ fontSize: "0.85rem", fontWeight: 600, display: "block", marginBottom: "6px" }}>
          Your photo
        </label>

        {previewSrc ? (
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <img
              src={previewSrc}
              alt="Author preview"
              style={{
                width: "64px",
                height: "64px",
                borderRadius: "50%",
                objectFit: "cover",
                border: "1px solid #eee",
              }}
            />
            <div>
              {uploading ? (
                <span style={{ fontSize: "0.85rem", color: "#666" }}>Uploading...</span>
              ) : imageUrl ? (
                <span style={{ fontSize: "0.85rem", color: "#22c55e" }}>Uploaded</span>
              ) : null}
              <button
                onClick={handleRemoveImage}
                style={{
                  display: "block",
                  background: "none",
                  border: "none",
                  color: "#999",
                  fontSize: "0.8rem",
                  cursor: "pointer",
                  padding: "2px 0",
                  marginTop: "2px",
                }}
              >
                Remove
              </button>
            </div>
          </div>
        ) : (
          <div
            onClick={() => fileInputRef.current?.click()}
            style={{
              border: "2px dashed #ddd",
              borderRadius: "10px",
              padding: "20px",
              textAlign: "center",
              cursor: "pointer",
              color: "#999",
              fontSize: "0.85rem",
              transition: "border-color 0.15s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.borderColor = "#aaa")}
            onMouseLeave={(e) => (e.currentTarget.style.borderColor = "#ddd")}
          >
            Click to upload a headshot
            <br />
            <span style={{ fontSize: "0.75rem" }}>JPG, PNG, or WebP, under 2MB</span>
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          style={{ display: "none" }}
        />

        {uploadError && (
          <p style={{ color: "#dc2626", fontSize: "0.8rem", marginTop: "6px" }}>
            {uploadError}
          </p>
        )}
      </div>

      {/* Continue / Skip */}
      <button
        onClick={handleContinue}
        disabled={isLoading || uploading}
        className="secondary-button"
        style={{
          width: "100%",
          padding: "12px",
          fontSize: "0.95rem",
          whiteSpace: "nowrap",
          maxWidth: "none",
          opacity: isLoading || uploading ? 0.5 : 1,
        }}
      >
        {hasAnyInput ? "Continue" : "Skip"}
      </button>
    </div>
  );
};

export default AuthorStep;
